import React, { useMemo } from 'react';

const clampNumber = (value, min, max) => Math.min(max, Math.max(min, value));

const hasValidCoordinates = (lat, lon) => {
  const latitude = Number(lat);
  const longitude = Number(lon);
  return Number.isFinite(latitude) && Number.isFinite(longitude) && latitude >= -90 && latitude <= 90 && longitude >= -180 && longitude <= 180;
};

const latLonToPixel = (lat, lon, zoom) => {
  const scale = 256 * Math.pow(2, zoom);
  const x = ((lon + 180) / 360) * scale;
  const sinLat = Math.sin((lat * Math.PI) / 180);
  const y = (0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI)) * scale;
  return { x, y };
};

const pixelToLatLon = (x, y, zoom) => {
  const scale = 256 * Math.pow(2, zoom);
  const lon = (x / scale) * 360 - 180;
  const n = Math.PI - (2 * Math.PI * y) / scale;
  const lat = (180 / Math.PI) * Math.atan(Math.sinh(n));
  return {
    latitude: clampNumber(lat, -85.05112878, 85.05112878),
    longitude: clampNumber(lon, -180, 180),
  };
};

const getMapEmbedUrl = (centerLat, centerLon, zoom = 16, width = 640, height = 280) => {
  const safeWidth = clampNumber(Math.round(width || 640), 320, 1200);
  const safeHeight = clampNumber(Math.round(height || 280), 180, 800);
  const centerPixel = latLonToPixel(centerLat, centerLon, zoom);
  const topLeft = pixelToLatLon(centerPixel.x - safeWidth / 2, centerPixel.y - safeHeight / 2, zoom);
  const bottomRight = pixelToLatLon(centerPixel.x + safeWidth / 2, centerPixel.y + safeHeight / 2, zoom);
  const minLon = topLeft.longitude;
  const minLat = bottomRight.latitude;
  const maxLon = bottomRight.longitude;
  const maxLat = topLeft.latitude;
  return `https://www.openstreetmap.org/export/embed.html?bbox=${minLon}%2C${minLat}%2C${maxLon}%2C${maxLat}&layer=mapnik`;
};

export default function LocationPreviewCard({
  latitude,
  longitude,
  title = 'Pratinjau Lokasi',
  emptyText = 'Koordinat belum tersedia. Gunakan lokasi terkini atau pilih di peta.',
  className = '',
  compact = true,
}) {
  const hasCoords = hasValidCoordinates(latitude, longitude);
  const latNumber = Number(latitude);
  const lonNumber = Number(longitude);
  const mapUrl = useMemo(() => {
    if (!hasCoords) return '';
    return getMapEmbedUrl(latNumber, lonNumber, compact ? 16 : 15, compact ? 560 : 900, compact ? 220 : 300);
  }, [hasCoords, latNumber, lonNumber, compact]);

  return (
    <section className={`cc-tonal-panel-soft p-3 ${className}`}>
      <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#93a8c1]">{title}</p>
      {hasCoords ? (
        <>
          <div className={`mt-2 overflow-hidden rounded-lg bg-[rgba(10,17,28,0.6)] ${compact ? 'h-40' : 'h-56'}`}>
            <iframe
              title="Location map preview"
              src={mapUrl}
              className="h-full w-full border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
          <p className="mt-2 text-[11px] text-[#8ea3bd]">
            Lat: <span className="font-mono text-[#d5e4f4]">{latNumber.toFixed(6)}</span>
            {' · '}
            Lng: <span className="font-mono text-[#d5e4f4]">{lonNumber.toFixed(6)}</span>
          </p>
        </>
      ) : (
        <p className="mt-2 text-xs text-[#7f93ac]">{emptyText}</p>
      )}
    </section>
  );
}

